import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import FaqAccordion from '../components/FaqAccordion';
import TipsList from '../components/TipsList';
import { downloadFile } from '../utils/download';
import {
  Download, ArrowRight, RotateCcw, FileText, FileEdit, FileSpreadsheet,
  TrendingUp, Image, FolderArchive, Check, XCircle, Folder
} from 'lucide-react';

const CONVERSION_META = {
  'pdf-to-word':  { title: 'PDF to Word', from: 'pdf', to: 'docx', icon: FileText, desc: 'Convert PDF to editable Word document', accept: { 'application/pdf': ['.pdf'] } },
  'word-to-pdf':  { title: 'Word to PDF', from: 'docx', to: 'pdf', icon: FileEdit, desc: 'Convert Word document to PDF', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'], 'application/msword': ['.doc'] } },
  'pdf-to-excel': { title: 'PDF to Excel', from: 'pdf', to: 'xlsx', icon: FileSpreadsheet, desc: 'Extract tables from PDF into Excel', accept: { 'application/pdf': ['.pdf'] } },
  'excel-to-pdf': { title: 'Excel to PDF', from: 'xlsx', to: 'pdf', icon: TrendingUp, desc: 'Convert Excel spreadsheet to PDF', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } },
  'pdf-to-jpg':   { title: 'PDF to JPG', from: 'pdf', to: 'jpg', icon: Image, desc: 'Convert PDF pages to JPG images', accept: { 'application/pdf': ['.pdf'] } },
  'jpg-to-pdf':   { title: 'JPG to PDF', from: 'jpg', to: 'pdf', icon: FolderArchive, desc: 'Create a PDF from JPG images', accept: { 'image/jpeg': ['.jpg', '.jpeg'] } },
};

// Temporarily disabled: these route through LibreOffice/soffice on the
// backend, which isn't installed on the current host, so the conversion
// fails server-side every time. Redirect home instead of letting anyone
// land on a tool that's guaranteed to fail. Remove once the office-
// conversion microservice (see office-service/) is live.
// pdf-to-jpg needs poppler (pdftoppm) on the server, which the current
// host doesn't have — remove it from this set once it's installed.
const DISABLED_TYPES = new Set(['pdf-to-word', 'word-to-pdf', 'pdf-to-excel', 'excel-to-pdf', 'pdf-to-jpg']);

const STATUS_MESSAGES = {
  pending: 'Waiting to start...',
  processing: 'Converting your file...',
  done: 'Done!',
  error: 'Conversion failed'
};

export default function ConvertPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const meta = CONVERSION_META[type];

  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null); // null | 'uploading' | 'processing' | 'done' | 'error'
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [outputFormat, setOutputFormat] = useState((meta && meta.to) || 'pdf');

  useEffect(() => {
    if (!meta || DISABLED_TYPES.has(type)) navigate('/');
    setFiles([]); setStatus(null); setResult(null); setError('');
    setOutputFormat((meta && meta.to) || 'pdf');
  }, [type]);

  if (!meta || DISABLED_TYPES.has(type)) return null;

  const handleConvert = async () => {
    if (!files.length) return toast.error('Please select a file first');
    trackEvent('conversion_started', { tool_name: meta.title });
    setStatus('uploading');
    setProgress(20);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('outputFormat', outputFormat);

    try {
      const sessionId = localStorage.getItem('sessionId') ||
  Math.random().toString(36).substring(2) + Date.now().toString(36);

      const res = await axios.post('/api/convert/single', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'x-session-id': sessionId }
      });

      const { conversionId } = res.data;
      setStatus('processing');
      setProgress(50);

      // Poll for result
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 60) { clearInterval(poll); setStatus('error'); setError('Conversion timed out'); return; }

        try {
          const statusRes = await axios.get(`/api/convert/status/${conversionId}`);
          const data = statusRes.data;

          if (data.status === 'done') {
            clearInterval(poll);
            setStatus('done');
            setProgress(100);
            setResult(data);
            toast.success('Conversion complete!');
          } else if (data.status === 'error') {
            clearInterval(poll);
            setStatus('error');
            setError(data.error || 'Conversion failed');
          } else {
            setProgress(prev => Math.min(prev + 5, 90));
          }
        } catch { clearInterval(poll); setStatus('error'); setError('Server error'); }
      }, 1500);

    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Upload failed');
      toast.error('Upload failed');
    }
  };

  const handleReset = () => {
    setFiles([]); setStatus(null); setResult(null); setError(''); setProgress(0);
  };

  return (
    <div>

       <Helmet>
        <title>{meta.title} — Free Online Converter | ILoveConvert</title>
        <meta name="description" content={`${meta.desc}. Free, fast, no signup needed. Convert ${meta.from.toUpperCase()} to ${meta.to.toUpperCase()} online.`} />
        <meta name="keywords" content={`${meta.from} to ${meta.to}, convert ${meta.from} to ${meta.to}, ${meta.from} to ${meta.to} online free, ${meta.title.toLowerCase()}`} />
        <link rel="canonical" href={`https://iloveconvert.in/convert/${type}`} />
        <meta property="og:title" content={`${meta.title} — Free Online | ILoveConvert`} />
        <meta property="og:description" content={meta.desc} />
        <meta property="og:url" content={`https://iloveconvert.in/convert/${type}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${meta.title} — Free Online | ILoveConvert`} />
        <meta name="twitter:description" content={meta.desc} />
      </Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><meta.icon size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', marginBottom: 8 }}>{meta.title}</h1>
        <p>{meta.desc}</p>
        <div className="container">
  <p>
    This {meta.title} converter is designed for fast and accurate file conversion.
    Whether you need to convert documents for work, study, or personal use,
    our tool ensures high-quality output with minimal effort.
  </p>
</div>
      </section>

      
       <section className="hero">
      {/* ── SEO Content Block — paste into ConvertPage.js and Home.js ── */}
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>

  {/* ── Stats row ─────────────────────────────────────────── */}
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 12, marginBottom: 48 }}>
    {[
      { num: '10+',  label: 'Supported formats' },
      // Hard-coded, not the real count — hidden until it shows a true number.
      // { num: '50+ K',label: 'Files converted' },
      { num: '2 hrs',label: 'Auto file deletion' },
      { num: '100%', label: 'Free forever' },
    ].map(s => (
      <div key={s.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{s.num}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</div>
      </div>
    ))}
  </div>

  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── How it works ──────────────────────────────────────── */}
 <h1>How it works <span>Instantly</span></h1>
  <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16, textAlign: 'left' }}>Convert any file in 4 simple steps</h2>
  <TipsList tips={[
      { label: 'Upload your file', tip: 'Drag and drop or click to browse. Supports files up to 50 MB. Works from desktop, tablet, or mobile.' },
      { label: 'Choose your output format', tip: 'Select from the available output formats. Options are filtered to what makes sense for your input file type.' },
      { label: 'Click Convert', tip: 'Processing happens on our secure servers. Most conversions complete in under 10 seconds.' },
      { label: 'Download your file', tip: 'Your converted file downloads directly. Both input and output files are deleted automatically after 2 hours.' },
    ]} />

  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── Supported formats ─────────────────────────────────── */}
   <h1>All formats  <span>we support</span></h1>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { title: 'PDF documents', tags: ['PDF → DOCX','PDF → XLSX','PDF → JPG','DOCX → PDF','XLSX → PDF','JPG → PDF'], desc: 'Convert PDFs to editable formats or create PDFs from Office documents and images.' },
      { title: 'Images',        tags: ['JPG → PNG','PNG → JPG','Any → WebP','Any → GIF','Any → BMP','Any → AVIF','Any → ICO'], desc: 'Convert between all major image formats with quality and resize controls.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{card.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {card.tags.map(t => (
            <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, border: '1px solid var(--border)', background: '#f8fafc', color: 'var(--text-muted)' }}>{t}</span>
          ))}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>

  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── Why choose us ─────────────────────────────────────── */}
   <h1>What makes this <span>converter different</span></h1>
  {/* <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Why use this tool</p> */}
  {/* <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>What makes this converter different</h2> */}
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { color: '#3B82F6', title: 'No software needed',  desc: 'Use it right from your browser. Nothing to install. Works on Windows, Mac, Linux, iOS and Android.' },
      { color: '#22C55E', title: 'Privacy first',        desc: 'Uploaded over HTTPS. Files deleted automatically within 2 hours. We never share your files.' },
      { color: '#F59E0B', title: 'High quality output',  desc: 'Powered by LibreOffice, Sharp, and pdf-lib — the same engines used by enterprise tools.' },
      { color: '#A855F7', title: 'Generous limits',     desc: 'Up to 50 MB per file. No watermarks. No premium plans. Free means free.' },
      { color: '#005CFC', title: 'Fast processing',      desc: 'Most conversions finish in under 10 seconds. No queue, no waiting.' },
      { color: '#0EA5E9', title: 'Mobile friendly',      desc: 'Full converter on phones and tablets. Upload from camera roll, download directly.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderLeft: `3px solid ${card.color}`, borderRadius: '0 12px 12px 0', padding: '14px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{card.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>

  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── Pro tips ──────────────────────────────────────────── */}
   <h1>Tips for best  <span>results</span></h1>
   <TipsList tips={[
      // PDF to Word and Compress PDF tips hidden while those tools are off.
      // { label: 'PDF to Word',        tip: 'PDFs with selectable text convert far better than scanned documents. Scanned PDFs need OCR for editable output.' },
      { label: 'Reduce image size',  tip: 'Convert JPG or PNG to WebP to reduce file size by 25–35% with minimal visible quality loss for most images — ideal for websites and email.' },
      { label: 'Images to PDF',      tip: 'Use JPG to PDF to combine multiple images into one document — great for sharing photo albums or scanned paperwork.' },
      // { label: 'Compress PDF',       tip: 'Use Compress PDF before emailing large files. Typically reduces size by 30–60% with no visible quality change.' },
      { label: 'Scanned text',       tip: 'Need the words from a photo or scan? Image to Text (OCR) turns them into text you can copy and edit.' },
    ]} />

  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── FAQ ───────────────────────────────────────────────── */}
   <h1>Frequently asked  <span>questions</span></h1>
   <FaqAccordion faqs={[
      { q: 'Is this file converter really free?',
        a: 'Yes. All tools on ILoveConvert are completely free with no hidden charges, no watermarks, and no premium plans required. Files are limited to 50 MB each to keep conversions fast.' },
      { q: 'Are my uploaded files kept private?',
        a: 'Yes. Files are transferred over HTTPS and stored temporarily only for conversion. Both input and output files are permanently deleted after 2 hours. We never analyse, share, or sell your files.' },
      { q: 'What is the maximum file size?',
        a: 'The current limit is 50 MB per file — enough for the vast majority of everyday documents and images. If your file is larger, try splitting it into smaller parts first.' },
      { q: 'Do I need to create an account?',
        a: 'No. Convert files without registering. A free account gives you conversion history and re-download access, but it is entirely optional.' },
      // PDF → Word is switched off, so this FAQ is hidden for now.
      // { q: 'Why does my converted Word file look different from the PDF?',
      //   a: 'PDF to Word conversion works best on text-based PDFs. Scanned PDFs and those with complex layouts (multi-column, tables) may lose some formatting during conversion.' },
      { q: 'Can I convert files on my iPhone or Android?',
        a: 'Yes. The converter is fully responsive. Upload from your camera roll or storage, convert, and download the result directly on your phone.' },
    ]} />

</div>
</section>
<div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={meta.accept} />

            {files.length > 0 && (
              <div className="convert-options">
                <div className="option-row">
                  <span className="option-label">Output Format:</span>
                  <div className="format-grid">
                    {[meta.to].map(fmt => (
                      <button key={fmt} className={`format-btn ${outputFormat === fmt ? 'active' : ''}`} onClick={() => setOutputFormat(fmt)}>
                        .{fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 20 }}>
                  <button className="btn btn-primary btn-lg" onClick={handleConvert} style={{ width: '100%' }}>
                    <ArrowRight size={18} /> Convert to .{outputFormat.toUpperCase()}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Processing */}
        {(status === 'uploading' || status === 'processing') && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>{STATUS_MESSAGES[status]}</p>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{progress}% — Please wait...</p>
          </div>
        )}

        {/* Success */}
        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>Conversion Successful!</h3>
              <p>Your file has been converted. Download it below.</p>
              <button onClick={() => downloadFile(result.downloadUrl, result.outputFileName, meta.title)} className="download-btn">
                <Download size={18} /> Download .{outputFormat.toUpperCase()} File
              </button>
              <p style={{ marginTop: 16, fontSize: '0.75rem', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Folder size={14} /> {result.outputFileName} • {result.outputSize ? `${(result.outputSize / 1024).toFixed(1)} KB` : ''}
              </p>
              <ShareButtons toolLabel={meta.title} />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}>
                <RotateCcw size={16} /> Convert Another File
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center', marginTop: 20 }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Conversion Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}>
              <RotateCcw size={16} /> Try Again
            </button>
          </div>
        )}

       

      </div>
    </div>
  );
}
